import React, { useEffect, useState } from "react";
import { withSpan, captureUserInteraction } from "../../Apm";
import { associationService, restClient, integrationService } from "client-app-core";
import { selectCanManageSharedFeed } from "../Selectors/FeedSharingSelectors";
// components
import { Dialog } from "../../CBComponents";
import EntityDelete from "./components/EntityDelete";
import EntityShare from "./components/EntityShare";
import ShapeAssociation from "./components/ShapeAssociation";
import ImageViewer from "../../SharedComponents/ImageViewer";
import PinToDialog from "../../SharedComponents/PinToDialog";
import {
	FileWidget,
	RulesWidget,
	CamerasWidget,
	DetailsWidget,
	LayoutControls,
	AlertWidget,
	SummaryWidget,
	MarineTrafficParticularsWidget,
	Activities,
	DroneAssociation,
	DeviceManagerWidget
} from "../Widgets";

import EntityDialog from "./components/EntityDialog";

// error boundary
import ErrorBoundary from "../../ErrorBoundary";

// utility
import $ from "jquery";
import { Translate, getTranslation } from "orion-components/i18n";

import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { feedInfoSelector } from "orion-components/GlobalData/Selectors";
import { trackHistoryDuration } from "orion-components/AppState/Selectors";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";

//actions
import {
	unsubscribeFromFeed,
	startAttachmentStream,
	startActivityStream,
	startCamerasInRangeStream,
	startDevicesInRangeStream,
	startTrackHistoryStream,
	startRulesStream,
	removeSubscriber
} from "orion-components/ContextualData/Actions";
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import { setMapTools } from "orion-components/Map/Tools/Actions";
import {
	deleteShape,
	addRemoveFromCollections,
	addRemoveFromEvents,
	createCollection,
	unlinkEntities,
	shareEntityToOrg,
	unshareEntityToOrg
} from "../../SharedActions/entityProfileActions";

import { checkPermissionBasedOnFeedId, canManageByApplication } from "orion-components/Profiles/Selectors";
import { entityWidgetConfig, getCustomWidgetConfig } from "orion-components/Profiles/Selectors/EntityProfileSelectors";
import { selectShapeFeedId } from "orion-components/GlobalData/Actions";
import { selectShape } from "../../GlobalData/Shapes/actions";
import isEqual from "lodash/isEqual";

const propTypes = {
	locale: PropTypes.string,
	selectFloorPlanOn: PropTypes.func,
	readOnly: PropTypes.bool,
	forReplay: PropTypes.bool
};

const defaultProps = {
	locale: "en",
	selectFloorPlanOn: () => {},
	//The `readonly` and `forReplay` props are exclusively provided to this component by the `replay-app`. To ensure the proper functionality of other apps, it is necessary to set both `readonly` and `forReplay` to `false`.
	readOnly: false,
	forReplay: false
};

// cSpell:ignore mapstate mapstatus
const EntityProfile = props => {
	const { forReplay, appData, readOnly, endDate, selectFloorPlanOn, floorPlansWithFacFeed, floorPlanView } = props;
	const dispatch = useDispatch();

	const user = useSelector(state => state?.session?.user?.profile);
	const context = useSelector(state => selectedContextSelector(state));
	const entity = context?.entity;
	const dialog = useSelector(state => entity && state?.appState?.dialog?.openDialog);
	const dialogData = useSelector(state => entity && state?.appState?.dialog?.dialogData);
	const trackHistDuration = useSelector(state => entity && trackHistoryDuration(state));
	const entityType = entity?.entityType;
	const feedId = entity?.feedId;
	const displayProperties = useSelector(state => entity && feedInfoSelector(feedId)(state)?.displayProperties);
	const profileIconTemplate = useSelector(state => entity && feedInfoSelector(feedId)(state)?.profileIconTemplate);
	const marineTrafficVisible = useSelector(state => entity && feedInfoSelector(feedId)(state)?.marineTrafficVisible);
	const feedDisplayProps = entity && displayProperties;
	const notifications = useSelector(state => (entity ? state?.globalData?.notifications : {}));
	const contextId = entity?.id;
	const dir = useSelector(state => getDir(state));
	const floorPlansWithFacilityFeed = useSelector(state =>
		floorPlansWithFacFeed ? state.globalData.floorPlanWithFacilityFeedId.floorPlans : null
	);
	const canViewRules = useSelector(
		state => !readOnly && canManageByApplication(state, "rules-app", "canView", "config")
	);
	const canManageRules = useSelector(state => !readOnly && canManageByApplication(state, "rules-app", "manage"));
	const canManageEvents = useSelector(state => canManageByApplication(state, "events-app", "manage"));
	const canPinToCollections = useSelector(state => canManageByApplication(state, "map-app", "manage"));
	const canAccess = useSelector(state => !readOnly && checkPermissionBasedOnFeedId(state)(entity?.feedId, "config"));
	const canManage = useSelector(
		state => !readOnly && checkPermissionBasedOnFeedId(state)(entity?.feedId, "permissions", "manage")
	);
	const canDeleteFiles = useSelector(
		state => !readOnly && (entityType !== "track" ? canManage : canManageByApplication(state, "map-app", "manage"))
	);
	const integrations = useSelector(state => state.session?.user?.profile?.integrations || []);
	const canManageSharedFeed = useSelector(state => selectCanManageSharedFeed(state, entity?.feedId));
	const canEditDelete = canManage && canManageSharedFeed;

	const checkDrone = getCustomWidgetConfig("drone-association", context, integrations);

	const [layoutControlsOpen, setLayoutControlsOpen] = useState(false);
	const [scrolledUp, setScrolledUp] = useState(false);
	const [hiding, setHiding] = useState(false);
	const [zetronPhoneVisible, setZetronPhoneVisible] = useState(false);
	const [marineTrafficVesselData, setMarineTrafficVesselData] = useState(null);
	const [editing, setEditing] = useState(false);

	const { orgId } = user ?? {};
	const { attachments = [], rules = [] } = context ?? {};
	const { entityData } = entity ?? {};
	const { properties, geometry } = entityData ?? {};
	const { name, description, type, subtype, overrideName, overrideDescription } = properties ?? {};
	const isMarker = context && entityType === "shapes" && type === "Point";
	const isValidZone = (subtype ? subtype : type) === "Polygon";
	const devicesInRange = context?.devicesInRange || [];
	const camerasInRange = context?.camerasInRange || [];

	let disableEditAndDeleteSummaryAction = false;

	if (rules?.length > 0 && entityType === "shapes") {
		disableEditAndDeleteSummaryAction = true;
	}

	const imageAttachments =
		attachments?.filter(attachment => {
			return /(jpg)|(png)|(jpeg)|(gif)|(svg)/.exec(attachment?.mimeType);
		}) || [];

	const widgetState = useSelector(
		state =>
			entity && entityWidgetConfig(state)(context, marineTrafficVesselData, marineTrafficVisible, isValidZone),
		isEqual
	);

	useEffect(() => {
		if (marineTrafficVisible) {
			integrationService.getExternalSystemLookup(
				"marine-traffic",
				entity.entityType,
				(err, response) => {
					if (err) console.log("ERROR", err);
					if (!response) return;
					const { data } = response;
					// if error returned will be undefined
					setMarineTrafficVesselData(data ? data : {});
				},
				`sourceId=${entity.sourceId}&targetId=${entity.id}&targetType=${entity.entityType}`
			);
		}

		const serviceCallBackExternalSystem = (err, response) => {
			if (err) {
				if (!zetronPhoneVisible) {
					setZetronPhoneVisible(false);
				}
			} else {
				if (response.clientInstalled) {
					setZetronPhoneVisible(true);
				}
			}
		};

		if (contextId) {
			if (!forReplay) {
				dispatch(startActivityStream(contextId, entityType, "profile"));
				dispatch(startAttachmentStream(contextId, "profile"));
				dispatch(startCamerasInRangeStream(contextId, entityType, "profile"));
				if (isValidZone) dispatch(startDevicesInRangeStream(contextId, entityType, "profile"));
				dispatch(startRulesStream(contextId, "profile"));

				const { entityData, feedId } = context.entity;

				if (
					entityType === "track" &&
					feedId === "zetron" &&
					entityData &&
					entityData.properties &&
					entityData.properties.subtype &&
					entityData.properties.subtype.toLowerCase() === "zetron"
				) {
					/* Note:
						Suppose to get externalSystems [] from redux state (e.g. state.session.organization.externalSystems)
						but different apps profileContainer needs to be modified in order to pass it as props.
						therefore here fetching directly to avoid modifying multiple apps profile container.
					*/

					integrationService.getExternalSystem("zetron", (errExt, responseExt) => {
						if (errExt) {
							console.log("An error has occurred to check external system is available to user.");
						} else {
							if (responseExt && responseExt.externalSystemId) {
								integrationService.getExternalSystemLookup(
									"zetron",
									"serviceAvailableToUser",
									serviceCallBackExternalSystem
								);
							}
						}
					});
				}
			} else {
				// do we need to do something else here, or leave it up to the widgets to pull in the static data
			}
		}
	}, []);

	useEffect(() => {
		handleScroll();
	}, [scrolledUp]);

	const handleInitiateRadioCall = radioUnitId => {
		const dataToPost = { radioUnitId: radioUnitId };

		if (radioUnitId) {
			//post example: https://192.168.66.134/integration-app/api/externalSystem/zetron/resource/callRadio
			restClient.exec_post(
				"/integration-app/api/externalSystem/zetron/resource/callRadio",
				dataToPost,
				function (err, response) {
					//Note : At present we just fire and forget and incase of error display standard error message in console.
					if (err) {
						console.log("An error has occurred sending command to zetron interface.", err, response);
					}
				}
			);
		}
	};

	const handleScroll = () => {
		$(".cb-profile-wrapper").on("resize scroll", () => {
			const elementTop = $(".summary-wrapper").offset().top;
			const elementBottom = elementTop + $(".summary-wrapper").outerHeight() + 120; // offset for app/navigation bar
			const viewportTop = $(".cb-profile-wrapper").scrollTop();
			const profileHeight = $(".cb-profile-wrapper").height();
			const viewportBottom = viewportTop + $(".cb-profile-wrapper").height();
			const widgetsHeight = $(".widgets-container").height();
			const scrollLength = viewportBottom - elementBottom;
			const pctScrolled = Math.floor((viewportTop / scrollLength) * 100); // gets percentage scrolled
			if (!scrolledUp && pctScrolled > 1 && widgetsHeight > profileHeight - 66) {
				setScrolledUp(true);
			} else if (scrolledUp && pctScrolled < 2) {
				setScrolledUp(false);
			}
		});
	};

	const toggleTrackHistory = () => {
		if (context.trackHistory) {
			captureUserInteraction("EntityProfile Track History On");
			dispatch(removeSubscriber(contextId, "trackHistory", "map"));
			dispatch(unsubscribeFromFeed(contextId, "trackHistory", "profile", forReplay));
		} else {
			dispatch(startTrackHistoryStream(context.entity, "profile", trackHistDuration, forReplay));
		}
	};

	//this function is not called anywhere
	//const handleCloseEntityProfile = () => {
	//	hideInfo();
	//	updateViewingHistory([]);
	//};

	const handleEditLayout = event => {
		event.preventDefault();
		setLayoutControlsOpen(true);
	};

	const handleCloseEditLayout = () => {
		setLayoutControlsOpen(false);
	};

	const handleExpand = () => {
		setScrolledUp(false);
		$(".cb-profile-wrapper").scrollTop(0);
	};

	const handleShareClick = entity => {
		entity.isPublic ? dispatch(unshareEntityToOrg(entity.id)) : dispatch(shareEntityToOrg(entity.id));

		dispatch(closeDialog("shareEntityDialog"));
	};

	const getWidgetStatus = widgetId => {
		const widgetConfig = widgetState;
		const widget = widgetConfig.find((widget, index) => {
			widget.index = index;
			return widget.id === widgetId;
		});

		return widget;
	};

	/**
	 * Check association before opening a dialog, throwing an error or failed association dialog if entity is associated
	 * @param {string} dialogId -- Id of dialog you'd like to open if association checks are passed
	 * @param {string} associationAction -- "delete" or "unshare"
	 */
	const openDialogWithAssociation = (dialogId, associationAction) => {
		associationService.checkAssociations(contextId, (err, response) => {
			if (err || response.error) {
				dispatch(
					openDialog(
						"entity-profile-error",
						getTranslation("global.profiles.entityProfile.main.problemOccurred")
					)
				);
			} else if (response) {
				if (response.hasAssociations) {
					// Passing an action along so dialog knows what text to render
					dispatch(
						openDialog("shape-association", {
							...response,
							action: associationAction
						})
					);
				} else {
					dispatch(openDialog(dialogId));
				}
			}
		});
	};

	const handleEditMode = editing => {
		setEditing(editing);
	};

	const summaryWidgetActions = () => {
		let actions = [];

		if (zetronPhoneVisible) {
			actions = [
				...actions,
				{
					name: getTranslation("global.profiles.entityProfile.main.tetraRadioCall"),
					nameText: "Zetron Call",
					action: () => handleInitiateRadioCall(properties.radioUnitId)
				}
			];
		}

		actions = [
			...actions,
			{
				name: getTranslation("global.profiles.entityProfile.main.trackHistory"),
				nameText: "Track History",
				action: () => toggleTrackHistory(contextId)
			},
			{
				name: getTranslation("global.profiles.entityProfile.main.pinTo"),
				nameText: "Pin To",
				action: () => dispatch(openDialog("pinToDialog"))
			}
		];
		if (canEditDelete && !floorPlanView) {
			actions = [
				...actions,
				{
					name: getTranslation("global.profiles.entityProfile.main.edit"),
					nameText: "Edit",
					action: () => {
						if (entityType === "track") {
							handleEditMode(true);
							dispatch(openDialog("entityEditDialog"));
							return;
						}

						let drawMode = "direct_select";
						if (geometry.type === "Point") {
							if (feedId.includes("shape")) {
								dispatch(selectShapeFeedId(feedId));
								dispatch(selectShape(geometry.type));
							}
							drawMode = "simple_select";
						} else if (properties?.isRectangle) {
							if (feedId.includes("shape")) {
								dispatch(selectShapeFeedId(feedId));
								dispatch(selectShape("Rectangle"));
							}
							drawMode = "direct_select_rectangle";
						} else if (properties?.isCircle) {
							if (feedId.includes("shape")) {
								dispatch(selectShapeFeedId(feedId));
								dispatch(selectShape("Circle"));
							}
							drawMode = "direct_select_circle";
						} else {
							if (feedId.includes("shape")) {
								dispatch(selectShapeFeedId(feedId));
								dispatch(selectShape(geometry.type));
							}
						}

						dispatch(
							setMapTools({
								type: "drawing",
								mode: drawMode,
								feature: { id: contextId, ...entity.entityData }
							})
						);
					},
					disabled: disableEditAndDeleteSummaryAction
				}
			];
		}
		if (canEditDelete) {
			actions = [
				...actions,
				{
					name: getTranslation("global.profiles.entityProfile.main.delete"),
					nameText: "Delete",
					action: () => openDialogWithAssociation("shapeDeleteDialog", "delete"),
					disabled: disableEditAndDeleteSummaryAction
				}
			];
		}
		return actions;
	};
	const widgetRenderFilters = {
		details: !feedDisplayProps && entityType === "shapes",
		marineTrafficParticulars: !marineTrafficVesselData || !marineTrafficVisible,
		alerts: Object.values(notifications).length === 0,
		rules: !canViewRules && isMarker
	};

	widgetRenderFilters["drone-association"] = checkDrone.length === 0;
	widgetRenderFilters["device-manager"] = !isValidZone;

	const renderWidgets = widgetName => {
		switch (widgetName) {
			case "details":
				return (
					<DetailsWidget
						id={widgetName}
						key={`${contextId}-details`}
						details={{ ...properties, coordinates: geometry?.coordinates }}
						displayProps={feedDisplayProps}
						enabled={!widgetRenderFilters.details}
					/>
				);
			case "marineTrafficParticulars":
				return (
					<MarineTrafficParticularsWidget
						id={widgetName}
						data={marineTrafficVesselData}
						enabled={!widgetRenderFilters.marineTrafficParticulars}
					/>
				);
			case "cameras":
				return (
					<CamerasWidget
						id={widgetName}
						key={`${contextId}-cameras`}
						canLink={canManage}
						selectFloorPlanOn={selectFloorPlanOn}
						floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
						unlinkCameras={unlinkEntities}
						contextId={contextId}
						unsubscribeFromFeed={unsubscribeFromFeed}
						subscriberRef="profile"
						readOnly={readOnly}
						disableSlew={readOnly}
					/>
				);
			case "alerts":
				return <AlertWidget id={widgetName} key={`${contextId}-alerts`} contextId={contextId} />;
			case "activities":
				return (
					<Activities
						id={widgetName}
						key={`${contextId}-activities`}
						pageSize={5}
						canManage={canAccess}
						contextId={contextId}
						subscriberRef="profile"
						readOnly={readOnly}
						forReplay={forReplay}
						endDate={endDate}
						profile="entity"
					/>
				);
			case "files":
				return (
					<FileWidget
						id={widgetName}
						key={`${contextId}-files`}
						canDelete={canDeleteFiles}
						hasAccess={canAccess}
						contextId={contextId}
						subscriberRef="profile"
					/>
				);
			case "rules":
				return (
					<RulesWidget
						id={widgetName}
						key={`${contextId}-rules`}
						canManage={canManageRules}
						canViewRules={canViewRules}
						contextId={contextId}
						hasLinks={true}
						orgId={orgId}
						subscriberRef="profile"
					/>
				);
			case "drone-association":
				return (
					<DroneAssociation
						id={widgetName}
						key={`${contextId}-drone-association`}
						contextId={contextId}
						enabled={!widgetRenderFilters["drone-association"]}
					/>
				);
			case "device-manager":
				return <DeviceManagerWidget id={widgetName} devices={devicesInRange} cameras={camerasInRange} />;
			default:
				return null;
		}
	};

	if (context) {
		// Height of summary-info (consistent between scrolled and not scrolled state) + padding
		const scrollOffset = scrolledUp ? 167 : 0;
		// Dynamic offset for widget container when SummaryWidget is collapsed
		const widgetsContainerStyle = {
			top: scrollOffset
		};

		return (
			<div className="cb-profile-wrapper" style={{ height: "100%" }} data-testid="entity-profile">
				<div style={{ display: layoutControlsOpen ? "block" : "none" }} data-testid="entity-profile-layout-controls">
					<ErrorBoundary>
						<LayoutControls
							close={handleCloseEditLayout}
							widgetOrder={widgetState}
							profile="entity"
							layoutControlsOpen={layoutControlsOpen}
						/>
					</ErrorBoundary>
				</div>
				<div style={{ display: layoutControlsOpen ? "none" : "block" }}>
					{!scrolledUp && <ImageViewer images={imageAttachments} dir={dir} />}
					<ErrorBoundary>
						<SummaryWidget
							id={contextId}
							zetronPhoneVisible={zetronPhoneVisible}
							contextId={contextId}
							name={overrideName || name}
							type={subtype ? subtype : type}
							geometry={geometry}
							description={overrideDescription || description}
							scrolledUp={scrolledUp}
							handleExpand={handleExpand}
							profileIconTemplate={profileIconTemplate}
							actions={summaryWidgetActions()}
							readOnly={readOnly}
						/>
					</ErrorBoundary>
					{!scrolledUp && (
						<div className="layout-control-button" data-testid="entity-profile-layout-button-section">
							<a className="cb-font-link" onClick={handleEditLayout} data-testid="entity-profile-edit-layout-button">
								<Translate value="global.profiles.entityProfile.main.editLayout" />
							</a>
						</div>
					)}
					<div className="widgets-container" style={widgetsContainerStyle} data-testid="entity-profile-widgets">
						{widgetState
							.filter(element => getWidgetStatus(element.id))
							.map(element => {
								const { id, enabled } = element;

								if (widgetRenderFilters[id]) {
									return false;
								}
								if (!enabled) {
									return false;
								}
								return <ErrorBoundary key={id}>{renderWidgets(id)}</ErrorBoundary>;
							})}

						<PinToDialog
							close={() => dispatch(closeDialog("pinToDialog"))}
							entity={entity}
							canManageEvents={canManageEvents}
							canPinToCollections={canPinToCollections}
							addRemoveFromCollections={addRemoveFromCollections}
							addRemoveFromEvents={addRemoveFromEvents}
							createCollection={createCollection}
							openDialog={openDialog}
							closeDialog={closeDialog}
							dir={dir}
						/>
					</div>

					{canEditDelete && (
						<EntityDelete closeDialog={closeDialog} deleteShape={deleteShape} id={contextId} name={name} />
					)}
					<EntityShare
						handleClick={() => handleShareClick(entity, orgId)}
						handleClose={() => dispatch(closeDialog("shareEntityDialog"))}
						shared={entity.isPublic}
					/>
					<ShapeAssociation closeDialog={closeDialog} dialogData={dialogData} dir={dir} />
					<Dialog
						key="entity-profile-error"
						open={dialog === "entity-profile-error"}
						confirm={{
							label: getTranslation("global.profiles.entityProfile.main.ok"),
							action: () => {
								dispatch(closeDialog("entity-profile-error"));
							}
						}}
						textContent={dialogData}
						dir={dir} data-testid="orion-components-profiles-entity-profile-entity-profile-dialog"
					/>
				</div>
				<ErrorBoundary>
					<EntityDialog
						handleEditMode={handleEditMode}
						open={dialog === "entityEditDialog"}
						selectedEntity={entity}
						closeDialog={closeDialog}
						dir={dir}
					/>
				</ErrorBoundary>
			</div>
		);
	} else {
		return <div />;
	}
};

// span instead of transaction
EntityProfile.propTypes = propTypes;
EntityProfile.defaultProps = defaultProps;
export default withSpan("entity-profile", "profile")(EntityProfile);
